import {
  sendEmailVerification,
  sendPasswordResetEmail,
  verifyBeforeUpdateEmail,
  type ActionCodeSettings,
  type Auth,
  type User,
} from "firebase/auth";
import { interfaceLanguageController } from "../../i18n/interfaceLanguageRuntime";
import { matchLocaleTag, type LocaleCode } from "../../i18n/localeRegistry";

const pendingEmailByAuth = new WeakMap<Auth, Promise<void>>();

/** A BCP 47 request tag, not evidence of the provider's delivered template. */
export function firebaseEmailRequestLocale(requestedLocale: unknown): LocaleCode {
  return matchLocaleTag(requestedLocale) ?? "en";
}

function withEmailLanguage(
  auth: Auth,
  requestedLocale: unknown,
  send: () => Promise<void>
): Promise<void> {
  // Capture intent when the user requests the email, before waiting on a send.
  const locale = firebaseEmailRequestLocale(requestedLocale);
  const run = async () => {
    const previousLanguage = auth.languageCode;
    auth.languageCode = locale;
    try {
      // Firebase reads languageCode after token/header awaits. Keep it stable
      // through the complete SDK operation, including failure and reload.
      await send();
    } finally {
      // Preserve an explicit change made by another Auth integration meanwhile.
      if (auth.languageCode === locale) auth.languageCode = previousLanguage;
    }
  };
  const previous = pendingEmailByAuth.get(auth);
  const result = previous ? previous.then(run) : run();
  // A rejected send must not poison subsequent operations or be retried here.
  const settled = result.then(() => undefined, () => undefined);
  pendingEmailByAuth.set(auth, settled);
  void settled.then(() => {
    if (pendingEmailByAuth.get(auth) === settled) pendingEmailByAuth.delete(auth);
  });
  return result;
}

export function sendLocalizedPasswordResetEmail(
  auth: Auth,
  email: string,
  locale = interfaceLanguageController.getSnapshot().effectiveLocale,
  actionCodeSettings?: ActionCodeSettings
): Promise<void> {
  return withEmailLanguage(auth, locale, () => sendPasswordResetEmail(auth, email, actionCodeSettings));
}

export function sendLocalizedEmailVerification(
  auth: Auth,
  user: User,
  locale = interfaceLanguageController.getSnapshot().effectiveLocale,
  actionCodeSettings?: ActionCodeSettings
): Promise<void> {
  return withEmailLanguage(auth, locale, () => sendEmailVerification(user, actionCodeSettings));
}

export function verifyBeforeUpdateEmailLocalized(
  auth: Auth,
  user: User,
  newEmail: string,
  locale = interfaceLanguageController.getSnapshot().effectiveLocale,
  actionCodeSettings?: ActionCodeSettings
): Promise<void> {
  return withEmailLanguage(auth, locale, () => verifyBeforeUpdateEmail(user, newEmail, actionCodeSettings));
}
